import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import Button from '../../ui/Button';
import nurseryService from '../../../services/nursery';
import { errorMessage } from '../../../utils/apiResponse';
import { formatLocalDateInTz } from '../../../utils/timezone';
import { cn } from '../../../utils/cn';
import type { DespatchChoice, DespatchResult, LoadShortfall } from '../../../types/nursery';

/**
 * Despatch a picked load. Stock leaves from the lots it was picked from, the
 * orders ship, and whatever each order was short is listed here with what
 * happens to it: backordered by default, into the order's own window. A
 * Nursery Admin can instead move a backorder to another season or cancel the
 * remainder; everyone else sees the default only, with the reason why.
 */

export interface DespatchDialogProps {
  loadId: number;
  loadNumber: string;
  timezone: string;
  isAdmin: boolean;
  onClose: () => void;
  /** tellCustomers: the person asked to email the load's customers next. */
  onDespatched: (result: DespatchResult, tellCustomers: boolean) => void;
}

type Mode = 'backorder' | 'later' | 'cancel';

export function DespatchDialog({ loadId, loadNumber, timezone, isAdmin, onClose, onDespatched }: DespatchDialogProps) {
  const [shortfalls, setShortfalls] = useState<LoadShortfall[] | null>(null);
  const [modes, setModes] = useState<Record<number, Mode>>({});
  const [windows, setWindows] = useState<Record<number, { from: string; by: string }>>({});
  const [busy, setBusy] = useState(false);
  const [tell, setTell] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    nurseryService.getLoadShortfalls(loadId)
      .then((res) => (res.success ? setShortfalls(res.data) : setError(errorMessage(res, 'Could not load the shortfalls'))))
      .catch((e) => setError(errorMessage(e, 'Could not load the shortfalls')));
  }, [loadId]);

  const despatch = async () => {
    const choices: DespatchChoice[] = (shortfalls ?? []).flatMap((s): DespatchChoice[] => {
      const mode = modes[s.order_id] ?? 'backorder';
      if (mode === 'cancel') return [{ order_id: s.order_id, action: 'cancel' }];
      if (mode === 'later') {
        const w = windows[s.order_id];
        return [{ order_id: s.order_id, action: 'backorder', despatch_from: w?.from || null, despatch_by: w?.by || null }];
      }
      return [];
    });
    const missingWindow = (shortfalls ?? []).some((s) => modes[s.order_id] === 'later' && !windows[s.order_id]?.from);
    if (missingWindow) {
      setError('Give each backorder moved to another season the date it can go from.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await nurseryService.despatchLoad(loadId, choices);
      if (res.success) onDespatched(res.data, tell);
      else setError(errorMessage(res, 'Could not despatch the load'));
    } catch (e) {
      setError(errorMessage(e, 'Could not despatch the load'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4" role="dialog" aria-modal="true" aria-labelledby="despatch-title">
      <div className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-xl shadow-xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-3 px-5 pt-5">
          <div className="min-w-0">
            <h2 id="despatch-title" className="text-lg font-semibold text-neutral-900">Despatch {loadNumber}</h2>
            <p className="text-sm text-neutral-500">
              The trees leave the lots they were picked from and every order on the load is marked shipped.
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="p-1 rounded hover:bg-neutral-100 flex-shrink-0">
            <X className="h-5 w-5 text-neutral-500" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {error && <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700">{error}</div>}
          {shortfalls === null ? (
            !error && <div className="h-20 bg-neutral-100 rounded-lg animate-pulse" />
          ) : shortfalls.length === 0 ? (
            <p className="text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
              Every order is complete. Nothing to backorder.
            </p>
          ) : (
            <>
              <p className="text-sm text-neutral-700">
                {shortfalls.length} order{shortfalls.length === 1 ? ' is' : 's are'} short. What is short goes on a backorder
                {isAdmin ? ' unless you choose otherwise.' : '. Only a Nursery Admin can move it to another season or cancel it.'}
              </p>
              <ul className="space-y-3">
                {shortfalls.map((s) => {
                  const mode = modes[s.order_id] ?? 'backorder';
                  const short = s.lines.reduce((n, l) => n + l.short, 0);
                  return (
                    <li key={s.order_id} className="border border-neutral-200 rounded-lg p-3">
                      <p className="font-medium text-neutral-900 break-words">{s.order_number} · {s.customer_name}</p>
                      <ul className="mt-1 text-sm text-neutral-600 space-y-0.5">
                        {s.lines.map((l) => (
                          <li key={l.line_id} className="break-words">
                            {l.short} × {l.cultivar_name}{l.rootstock_name ? ` / ${l.rootstock_name}` : ''}, {l.tree_type_name}
                            <span className="text-neutral-400"> ({l.picked} of {l.ordered} sent)</span>
                          </li>
                        ))}
                      </ul>
                      {s.paid_online && (
                        <p className="text-xs text-neutral-500 mt-1">
                          Paid online: a backorder carries the payment for these trees. Cancelling them refunds
                          their price to the customer&rsquo;s card as soon as the load is despatched.
                        </p>
                      )}
                      {isAdmin && (
                        <div className="mt-2 flex flex-wrap gap-2" role="group" aria-label={`What happens to ${s.order_number}'s shortfall`}>
                          {([
                            ['backorder', `Backorder ${short}`],
                            ['later', 'Backorder into another season'],
                            ['cancel', 'Cancel the rest'],
                          ] as [Mode, string][]).map(([m, label]) => (
                            <button key={m} type="button" aria-pressed={mode === m}
                              onClick={() => setModes((x) => ({ ...x, [s.order_id]: m }))}
                              className={cn('rounded-lg border px-3 py-1.5 text-sm',
                                mode === m ? 'bg-primary-50 border-primary-400 text-primary-800 font-medium' : 'bg-white border-neutral-300 text-neutral-600')}>
                              {label}
                            </button>
                          ))}
                        </div>
                      )}
                      {mode === 'backorder' && (
                        <p className="text-xs text-neutral-500 mt-2">
                          {s.despatch_by
                            ? `The backorder keeps this order's window, due by ${formatLocalDateInTz(s.despatch_by, timezone)}.`
                            : "The backorder keeps this order's despatch window."}
                        </p>
                      )}
                      {mode === 'later' && (
                        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <div>
                            <label htmlFor={`bo-from-${s.order_id}`} className="block text-xs text-neutral-500 mb-1">Not before</label>
                            <input id={`bo-from-${s.order_id}`} type="date" value={windows[s.order_id]?.from ?? ''}
                              onChange={(e) => setWindows((w) => ({ ...w, [s.order_id]: { from: e.target.value, by: w[s.order_id]?.by ?? '' } }))}
                              className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
                          </div>
                          <div>
                            <label htmlFor={`bo-by-${s.order_id}`} className="block text-xs text-neutral-500 mb-1">Must go by</label>
                            <input id={`bo-by-${s.order_id}`} type="date" value={windows[s.order_id]?.by ?? ''}
                              min={windows[s.order_id]?.from || undefined}
                              onChange={(e) => setWindows((w) => ({ ...w, [s.order_id]: { from: w[s.order_id]?.from ?? '', by: e.target.value } }))}
                              className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
                          </div>
                        </div>
                      )}
                      {mode === 'cancel' && (
                        <p className="text-xs text-amber-800 mt-2">The {short} short will not be sent. The customer should be told.</p>
                      )}
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>

        <label className="flex items-start gap-2 px-5 pb-3 text-sm text-neutral-700">
          <input type="checkbox" className="mt-0.5" checked={tell} onChange={(e) => setTell(e.target.checked)} />
          <span>Then email the customers that their trees are on the way (you will see who gets what before it sends)</span>
        </label>
        <div className="flex flex-wrap justify-end gap-2 px-5 pb-5">
          <Button variant="outline" size="sm" onClick={onClose} disabled={busy}>Not yet</Button>
          <Button size="sm" onClick={despatch} disabled={busy || shortfalls === null}>{busy ? 'Despatching…' : 'Despatch now'}</Button>
        </div>
      </div>
    </div>
  );
}

export default DespatchDialog;
